import React, { useEffect, useState } from 'react';
import { canJoinEventMeet, formatLiveCountdown } from '../../store/eventStore';
import { openGoogleMeet } from '../../utils/openGoogleMeet';

type Props = {
  meetUrl: string;
  startAt: Date | null;
  timeLabel?: string;
  className?: string;
};

const VideoIcon = ({ size = 14, className = '' }: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect width="15" height="14" x="1" y="5" rx="2" ry="2" />
  </svg>
);

/**
 * 1:1-Coaching: gleicher Meet-Flow wie Gruppen-Events
 * (Popup auf Desktop, ab 15 Min. vor Start freigeschaltet).
 */
const CoachingMeetJoinButton: React.FC<Props> = ({
  meetUrl,
  startAt,
  timeLabel = '',
  className = '',
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = () => setNow(Date.now());
    tick();
    const id = window.setInterval(tick, 15_000);
    return () => window.clearInterval(id);
  }, []);

  const url = String(meetUrl || '').trim();
  if (!url) return null;

  const info = formatLiveCountdown(startAt, new Date(now));
  const canJoin = canJoinEventMeet(startAt, new Date(now));
  const isEnded = info?.kind === 'ended';
  const disabled = !canJoin;

  let label: string;
  if (isEnded) {
    label = 'Termin beendet';
  } else if (canJoin) {
    label = 'Meeting beitreten';
  } else if (timeLabel) {
    label = info?.label ? `Startet ${timeLabel} · ${info.label}` : `Startet um ${timeLabel}`;
  } else {
    label = info?.label || 'Noch nicht gestartet';
  }

  const base =
    'mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors border';
  const activeCls =
    `${base} bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/40 text-emerald-300`;
  const disabledCls =
    `${base} bg-pe-elevated/60 dark:bg-slate-800/50 border-pe-border dark:border-slate-700 text-pe-muted opacity-50 cursor-not-allowed`;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!canJoin) return;
        openGoogleMeet(url);
      }}
      className={`${disabled ? disabledCls : activeCls} ${className}`}
      title={disabled ? label : 'Google Meet öffnen (ab 15 Min. vor Start)'}
    >
      <VideoIcon size={14} className={disabled ? 'text-pe-muted' : 'text-emerald-300'} />
      {label}
    </button>
  );
};

export default CoachingMeetJoinButton;
